import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import DescriptionWithLogo from './components/DescriptionWithLogo';
import BusinessHours from './components/BusinessHours/index';
import AdditionalInfo from './components/AdditionalInfo';
import Collapse from '@material-ui/core/Collapse';
import {
  biggerThan,
  biggerThanOrEqualTo,
  smallerThan,
  smallerThanOrEqualTo,
} from '../../../core/util/styledComponents';
import PanelCard from '../PanelCards/components/PanelCard';
import { analyticsTrackEvent } from '../../../core/analytics';
import {
  CDP_SHOW_MORE_DESCRIPTION,
  CDP_SHOW_LESS_DESCRIPTION,
} from '../../../core/analytics/events';
import { colors } from '../../../constants/colors';
import { fontSize } from '../../../constants/text';
import BrandAffiliation from './components/BrandAffiliation';
import { isTraditionalLocation } from '../../../core/util/location';
import { hasCustomDescription } from '../CDPInfoFactory/util';
import { safeGet } from '../../../core/util/object';

const Root = styled.div`
  width: 100%;
`;

const StyledDescription = styled(DescriptionWithLogo)`
  div p:nth-child(1) {
    margin-top: 0;
  }
  ${biggerThanOrEqualTo.md`
    display: flex;
  `}
`;

const StyledBusinessHours = styled(BusinessHours)`
  margin-top: 21px;
  margin-left: 125px;
  ${smallerThan.md`
    max-width: 100%;
    width: 100%;
    margin: 15px 0 0 0;
  `}
`;

const StyledAdditionalInfo = styled(AdditionalInfo)`
  margin-top: 32px;
  margin-bottom: 14px;
  margin-left: 125px;
  ${smallerThan.md`
    max-width: 100%;
    width: 100%;
    margin: 15px 0 0 0;
  `}
`;

const StyledCollapse = styled(Collapse)`
  ${props =>
    !props.in &&
    css`
      ::before {
        position: absolute;
        height: ${props.collapsedHeight};
        width: 100%;
        content: '';
        background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 35%);
        pointer-events: none; /* so the text is still selectable */
      }
    `}
`;

const MoreOrLess = styled.span`
  color: ${colors.link};
  font-size: ${fontSize.mediumSmall};
  cursor: pointer;

  ${biggerThan.sm`
    padding-left: 125px;
  `}

  ${smallerThanOrEqualTo.sm`
    padding-left: ${props => (props.expanded ? 0 : 125)}px;
  `}
`;

export default class AboutCard extends PureComponent {
  state = { expanded: false };

  handleMoreOrLessClick = () => {
    const event = this.state.expanded ? CDP_SHOW_LESS_DESCRIPTION : CDP_SHOW_MORE_DESCRIPTION;
    const {
      location: { id, uclId },
    } = this.props;
    analyticsTrackEvent(event, { locationId: id, uclId });

    this.setState(state => ({ expanded: !state.expanded }));
  };

  shouldShowMoreOrLess = () => {
    const { location } = this.props;
    if (isTraditionalLocation(location)) {
      return hasCustomDescription(location);
    }
    return true;
  };

  render = () => {
    const {
      location,
      enableCollapsing,
      defaultExpanded,
      nearbyLocations,
      isBookingFlow,
      sessionTrackingProperties,
    } = this.props;
    const { displayNamePrimary } = location;
    const { expanded } = this.state;
    const userAgent = safeGet(sessionTrackingProperties)('userAgent');

    return (
      <PanelCard title={`About ${displayNamePrimary}`} {...{ enableCollapsing, defaultExpanded }}>
        <Root>
          <StyledCollapse in={expanded} collapsedHeight="110px" timeout={{ enter: 400, exit: 0 }}>
            <StyledDescription
              location={location}
              nearbyLocations={nearbyLocations}
              clinicDescriptionOnly={isBookingFlow}
              userAgent={userAgent}
            />
          </StyledCollapse>
          {this.shouldShowMoreOrLess() && (
            <MoreOrLess expanded={expanded} onClick={this.handleMoreOrLessClick}>
              {expanded ? 'Show Less' : 'Read More'}
            </MoreOrLess>
          )}
          <BrandAffiliation location={location} userAgent={userAgent} />
          <StyledBusinessHours location={location} />
          <StyledAdditionalInfo location={location} />
        </Root>
      </PanelCard>
    );
  };
}

AboutCard.propTypes = {
  location: PropTypes.object.isRequired,
  nearbyLocations: PropTypes.object,
  defaultExpanded: PropTypes.bool,
  enableCollapsing: PropTypes.bool,
  isBookingFlow: PropTypes.bool,
  sessionTrackingProperties: PropTypes.object,
};
